import { Fix64 } from "../maths/fix64";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { StrikeTarget } from "./petKit";
import { applyPetHit } from "./petStatus";
import type { RaiderAt } from "./petFighter";
import type { PetSystem } from "./petSystem";

/**
 * A Pets v3 attack (docs/PETS.md "Pets v3": Swoop, Pounce, Shell Bash, Bubble Spit, Peck): every `intervalTicks` the
 * raider nearest the flag's cell within `reach` takes its hit (sim/petStatus.ts). Who it may pick: ground raiders, any
 * raider, or a flyer when one is in reach (the owl). A flyer takes `airMultPercent` of the damage. Burrowers underground
 * and flyers over a bridge are never in reach (the towers' targeting rule). PetBit, as the pup's bite.
 */
export function tickStrike(sim: LevelSim, pet: PetSystem): void {
  const s = pet.rule.strike;
  if (!s) return;
  if (pet.strikeCooldown > 0) { pet.strikeCooldown--; return; }
  const target = strikeTarget(sim, s.reach, s.targets);
  if (target === null) return;
  pet.strikeCooldown = s.intervalTicks;
  const damage = target.isAir && s.airMultPercent !== PERCENT ? s.hit.damage.mulInt(s.airMultPercent).divInt(PERCENT) : s.hit.damage;
  sim.events.add(SimEventKind.PetBit, target.id, damage.floorToInt());
  applyPetHit(sim, target, s.hit, damage);
}

const PERCENT = 100;

/** The raider an attack picks (see tickStrike), or null. */
export function strikeTarget(sim: LevelSim, reach: Fix64, targets: StrikeTarget, at?: RaiderAt): Enemy | null {
  if (targets === StrikeTarget.AirFirst) return nearest(sim, reach, true, true, at) ?? nearest(sim, reach, false, false, at);
  return nearest(sim, reach, targets === StrikeTarget.Any, false, at);
}

/** The raider nearest the flag's cell within `reach` (flyers too when `air`, only flyers when `onlyAir`), ties to the lower id. */
function nearest(sim: LevelSim, reach: Fix64, air: boolean, onlyAir: boolean, at?: RaiderAt): Enemy | null {
  const r = sim.troops.rally, fx = Fix64.fromInt(r.x), fy = Fix64.fromInt(r.y), r2 = reach.mul(reach), cells = reach.ceilToInt() + 1;
  let best: Enemy | null = null, bestD2 = Fix64.Zero;
  for (const e of sim.enemySlots) {
    if (!e.alive || (e.isAir ? !air : onlyAir)) continue;
    const p = at ? at(e) : e;
    if (p === null || Math.abs(p.cx - r.x) > cells || Math.abs(p.cy - r.y) > cells || !sim.targetable(e)) continue;
    const dx = p.x.sub(fx), dy = p.y.sub(fy), d2 = dx.mul(dx).add(dy.mul(dy));
    if (d2.gt(r2)) continue;
    if (best === null || d2.lt(bestD2) || (d2.eq(bestD2) && e.id < best.id)) { best = e; bestD2 = d2; }
  }
  return best;
}
